import React from 'react';
import './AppShellSkeleton.css';

/** Nav rows in the collapsed rail: roughly the account menu's length. */
const RAIL_NAV_ROWS = 5;

interface AppShellSkeletonProps {
  /** The screen's own skeleton, drawn where the page content goes. */
  children: React.ReactNode;
}

/**
 * The app chrome while MainLayout's lazy chunk loads — the collapsed sidebar
 * on desktop, the top bar on mobile — around the screen's own skeleton.
 *
 * Without it the first paint of a cold load (refresh) was the page skeleton
 * alone on a blank screen, and the sidebar popped in afterwards. Sizes mirror
 * the real chrome: the Sider's 72px collapsed width (SidebarMenu) and
 * MobileHeader's fixed 56px bar, switched at the same `md` (768px) breakpoint
 * MainLayout uses. CSS media queries, not antd's Grid: this renders before
 * the layout code has loaded.
 */
export function AppShellSkeleton({ children }: AppShellSkeletonProps): React.ReactElement {
  return (
    <div className="app-shell-skeleton" aria-busy="true">
      <aside className="app-shell-skeleton__sider" aria-hidden="true">
        <span className="app-shell-skeleton__bone app-shell-skeleton__logo" />
        <span className="app-shell-skeleton__divider" />
        <span className="app-shell-skeleton__bone app-shell-skeleton__avatar" />
        <span className="app-shell-skeleton__bone app-shell-skeleton__icon" />
        <span className="app-shell-skeleton__divider" />
        <span className="app-shell-skeleton__bone app-shell-skeleton__icon" />
        {Array.from({ length: RAIL_NAV_ROWS }, (_, index) => (
          <span key={index} className="app-shell-skeleton__bone app-shell-skeleton__icon" />
        ))}
      </aside>

      <header className="app-shell-skeleton__topbar" aria-hidden="true">
        <span className="app-shell-skeleton__bone app-shell-skeleton__topbar-logo" />
        <span className="app-shell-skeleton__bone app-shell-skeleton__icon" />
      </header>

      <main className="app-shell-skeleton__content">{children}</main>
    </div>
  );
}
